import { Device, User } from "@fairfleet/geotab";
import {
  TachographDeviceInfo,
  TachographDriverRow,
  TachographFile,
  TachographManufacturer,
  TachographRow,
} from "../models/Tachograph";
import { formatDate } from "./tachographDates";
import { extractDriverCardNumber, formatDriverName } from "./tachographDrivers";
import { getFileReferenceDate, getFileUploadDate, hasFileData } from "./tachographFiles";
import {
  combineOverallStatus,
  deriveConnectivityStatus,
  deriveStatusFromReferenceDate,
  getRowCategory,
} from "./tachographStatus";
import { buildTimeline } from "./tachographTimeline";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";

type TranslateCount = (key: string, options: { count: number }) => string;

type VehicleRowsParams = {
  devices: Device[];
  driverIdByDeviceId: Record<string, string | undefined>;
  driverById: Map<string, User>;
  fileByDeviceId: Record<string, TachographFile>;
  signalByDeviceId: Map<string, Date | null>;
  infoByDeviceId: Map<string, TachographDeviceInfo>;
  formatManufacturer: (manufacturer: TachographManufacturer) => string;
  intervalDays: number;
  language: string;
  now: Date;
  t: TranslateCount;
};

/**
 * One row per vehicle that says something about its tachograph. Vehicles without a readout
 * and without a tachograph signal are left out — they have no tachograph to report on.
 */
export function buildVehicleRows({
  devices,
  driverIdByDeviceId,
  driverById,
  fileByDeviceId,
  signalByDeviceId,
  infoByDeviceId,
  formatManufacturer,
  intervalDays,
  language,
  now,
  t,
}: VehicleRowsParams): TachographRow[] {
  const rows: TachographRow[] = [];

  for (const device of devices) {
    const latestFile = fileByDeviceId[device.id];
    const latestSignal = signalByDeviceId.get(device.id) ?? null;
    if (!hasFileData(latestFile) && !latestSignal) continue;

    const referenceDate = getFileReferenceDate(latestFile);
    const downloadStatus = deriveStatusFromReferenceDate(referenceDate, intervalDays, now);

    // Geotab exposes no inspection or company card date per vehicle, so both mirror the
    // download status and only say whether a readout arrived at all.
    const inspectionStatus = hasFileData(latestFile) ? downloadStatus : "warning";
    const companyCardStatus = inspectionStatus;
    const connectorStatus = deriveConnectivityStatus(latestSignal, now);

    const driverId = driverIdByDeviceId[device.id];
    const driver = driverId ? driverById.get(driverId) : undefined;
    const timeline = buildTimeline(
      referenceDate,
      getFileUploadDate(latestFile),
      intervalDays,
      language,
      now,
      t,
    );

    const info = infoByDeviceId.get(device.id);

    rows.push({
      id: device.id,
      summary: device.name,
      driver: formatDriverName(driver),
      licensePlate: getDeviceLicensePlate(device),
      driverId,
      manufacturer: info?.manufacturer ? formatManufacturer(info.manufacturer) : "",
      ...timeline,
      calibrationExpiryDate: info?.nextCalibration
        ? formatDate(info.nextCalibration, language)
        : timeline.calibrationExpiryDate,
      status: combineOverallStatus(downloadStatus, inspectionStatus, companyCardStatus),
      downloadStatus,
      inspectionStatus,
      companyCardStatus,
      connectorStatus,
      category: getRowCategory(downloadStatus, inspectionStatus, companyCardStatus, connectorStatus),
    });
  }

  return rows;
}

type DriverRowsParams = {
  drivers: User[];
  deviceIdByDriverId: Map<string, string>;
  licensePlateByDeviceId: Map<string, string>;
  fileByDriverId: Record<string, TachographFile>;
  signalByDeviceId: Map<string, Date | null>;
  intervalDays: number;
  language: string;
  now: Date;
  t: TranslateCount;
};

/** One row per driver with a card readout or with a vehicle that reports a tachograph. */
export function buildDriverRows({
  drivers,
  deviceIdByDriverId,
  licensePlateByDeviceId,
  fileByDriverId,
  signalByDeviceId,
  intervalDays,
  language,
  now,
  t,
}: DriverRowsParams): TachographDriverRow[] {
  const rows: TachographDriverRow[] = [];

  for (const driver of drivers) {
    const assignedDeviceId = deviceIdByDriverId.get(driver.id);
    const latestFile = fileByDriverId[driver.id];
    const hasSignal = !!assignedDeviceId && !!signalByDeviceId.get(assignedDeviceId);
    if (!hasFileData(latestFile) && !hasSignal) continue;

    const referenceDate = getFileReferenceDate(latestFile);

    rows.push({
      id: driver.id,
      driver: formatDriverName(driver),
      driverCardNumber: extractDriverCardNumber(driver),
      licensePlate: assignedDeviceId ? (licensePlateByDeviceId.get(assignedDeviceId) ?? "") : "",
      ...buildTimeline(
        referenceDate,
        getFileUploadDate(latestFile),
        intervalDays,
        language,
        now,
        t,
      ),
    });
  }

  return rows;
}
